import React, { useMemo, useContext, useState } from 'react'
import { DesignContext } from 'contexts/DesignContext'
import { rankWith, and, scopeEndsWith } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'

import ComponentWrapper from 'designer/component/display/layout/control/ComponentWrapper'
import DragContainerWrapper from 'designer/component/system/layout/control/DragContainerWrapper'
import PanelSample from 'designer/component/container/dynamic/Panel'
import PanelEditor from 'designer/component/container/dynamic/Editor'
import CustomHeader from 'designer/component/container/dynamic/Header'

import { Nav, Tab, Row, Col } from 'react-bootstrap'

import {
  createDisplayRenderer,
  createSystemRenderer,
} from 'designer/component/control/renderer'

import objectPath from 'object-path'

const dynamicTester = rankWith(
  3,
  and(scopeEndsWith('dynamic'), (schema) => !schema.subGroup)
)

const subDynamicTester = rankWith(
  3,
  and(scopeEndsWith('dynamic'), (schema) => schema.subGroup)
)

let dynamicActiveTabs = {}

const useDynamicActiveTab = (uischema) => {
  const [activeTab, setActiveTab] = useState(
    dynamicActiveTabs[uischema.key] || 'sample'
  )

  const onSelectTab = (tab) => {
    if (!tab) return
    dynamicActiveTabs[uischema.key] = tab
    setActiveTab(tab)
  }

  return { activeTab, onSelectTab }
}

const createFallbackPanel = (uischema, tagname) => {
  let key = `${uischema.key}${tagname}`
  let name = `${uischema.name}${tagname}`

  return {
    ...uischema,
    key,
    name,
    tagname,
    type: 'Group',
    uiType: 'container',
    scope: '#/properties/' + name + '/' + tagname,
    elements: [],
  }
}

const getDynamicElements = (uischema) => {
  let elements = Array.isArray(uischema.elements) ? uischema.elements : []
  if (elements.length > 0) return elements

  return [
    createFallbackPanel(uischema, 'dynamicPanel'),
    createFallbackPanel(uischema, 'editPanel'),
  ]
}

export const DynamicSystem = (props) => {
  const designContext = useContext(DesignContext)
  const { designerUi } = designContext
  let { schema, uischema } = props
  let { activeTab, onSelectTab } = useDynamicActiveTab(uischema)

  let elements = getDynamicElements(uischema)
  let showUi = elements[0] || {}
  let showSchema = objectPath.get(
    schema,
    `properties.${uischema.name}.properties.${showUi.name}`
  )
  let showProps = {
    ...props,
    panelSchema: showSchema,
    panelUi: showUi,
  }

  let editUi = elements[1] || {}
  let editSchema = objectPath.get(
    schema,
    `properties.${uischema.name}.properties.${editUi.name}`
  )

  let editProps = {
    ...props,
    panelSchema: editSchema,
    panelUi: editUi,
    designerUi: designerUi,
  }

  let allPanelProps = {
    ...props,
    showSchema: showSchema,
    showUi: showUi,
    editSchema: editSchema,
    editUi: editUi,
    designerUi: designerUi,
  }

  const renderers = useMemo(
    () => createSystemRenderer(elements),
    [JSON.stringify(elements)]
  )

  return (
    <DragContainerWrapper {...props}>
      <CustomHeader {...allPanelProps}>
        <Tab.Container activeKey={activeTab} onSelect={onSelectTab}>
          {editUi?.key ? (
            <Row className="w-100 mx-auto mt-3">
              <Col sm={12} className="p-0">
                <Nav variant="tabs" className="w-100 mx-auto mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey={'sample'}>หน้าตัวอย่าง</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey={showUi.key}>ออกแบบ Panel แสดง</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey={editUi.key}>
                      ออกแบบ Panel แก้ไข
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col sm={12} className="p-0">
                <Tab.Content className="w-100 mx-auto">
                  <Tab.Pane eventKey="sample">
                    <PanelSample {...allPanelProps} renderers={renderers} />
                  </Tab.Pane>
                  <Tab.Pane eventKey={showUi.key}>
                    <PanelEditor {...showProps} renderers={renderers} />
                  </Tab.Pane>
                  <Tab.Pane eventKey={editUi.key}>
                    <PanelEditor {...editProps} renderers={renderers} />
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          ) : (
            <PanelEditor {...showProps} renderers={renderers} />
          )}
        </Tab.Container>
      </CustomHeader>
    </DragContainerWrapper>
  )
}

export const SubDynamicSystem = (props) => {
  const designContext = useContext(DesignContext)
  const { designerUi } = designContext
  let { schema, uischema } = props
  let { activeTab, onSelectTab } = useDynamicActiveTab(uischema)

  let elements = getDynamicElements(uischema)
  let showUi = elements[0] || {}
  let showSchema = objectPath.get(
    schema,
    `properties.${uischema.name}.properties.${showUi.name}`
  )
  let showProps = {
    ...props,
    panelSchema: showSchema,
    panelUi: showUi,
  }

  let editUi = elements[1] || {}
  let editSchema = objectPath.get(
    schema,
    `properties.${uischema.name}.properties.${editUi.name}`
  )

  let editProps = {
    ...props,
    panelSchema: editSchema,
    panelUi: editUi,
    designerUi: designerUi,
  }

  let allPanelProps = {
    ...props,
    showSchema: showSchema,
    showUi: showUi,
    editSchema: editSchema,
    editUi: editUi,
    designerUi: designerUi,
  }

  const renderers = useMemo(
    () => createSystemRenderer(elements),
    [JSON.stringify(elements)]
  )

  return (
    <DragContainerWrapper {...props}>
      <CustomHeader {...allPanelProps}>
        <Tab.Container activeKey={activeTab} onSelect={onSelectTab}>
          {editUi?.key ? (
            <Row className="w-100 mx-auto mt-3">
              <Col sm={12} className="p-0">
                <Nav variant="tabs" className="w-100 mx-auto mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey={'sample'}>หน้าตัวอย่าง</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey={showUi.key}>ออกแบบ Panel แสดง</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey={editUi.key}>
                      ออกแบบ Panel แก้ไข
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col sm={12} className="p-0">
                <Tab.Content className="w-100 mx-auto">
                  <Tab.Pane eventKey="sample">
                    <PanelSample {...allPanelProps} renderers={renderers} />
                  </Tab.Pane>
                  <Tab.Pane eventKey={showUi.key}>
                    <PanelEditor {...showProps} renderers={renderers} />
                  </Tab.Pane>
                  <Tab.Pane eventKey={editUi.key}>
                    <PanelEditor {...editProps} renderers={renderers} />
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          ) : (
            <PanelEditor {...showProps} renderers={renderers} />
          )}
        </Tab.Container>
      </CustomHeader>
    </DragContainerWrapper>
  )
}

export const DynamicDisplay = (props) => {
  const designContext = useContext(DesignContext)
  const { designerUi } = designContext
  let { schema, uischema } = props
  let elements = getDynamicElements(uischema)
  const renderers = useMemo(
    () => createDisplayRenderer(elements),
    [JSON.stringify(elements)]
  )

  let showUi = elements[0] || {}
  let showSchema = objectPath.get(
    schema,
    `properties.${uischema.name}.properties.${showUi.name}`
  )

  let editUi = elements[1] || {}
  let editSchema = objectPath.get(
    schema,
    `properties.${uischema.name}.properties.${editUi.name}`
  )

  let allPanelProps = {
    ...props,
    showSchema: showSchema,
    showUi: showUi,
    editSchema: editSchema,
    editUi: editUi,
    designerUi: designerUi,
  }

  return (
    <ComponentWrapper {...props}>
      <CustomHeader {...allPanelProps}>
        <PanelSample {...allPanelProps} renderers={renderers} />
      </CustomHeader>
    </ComponentWrapper>
  )
}

export const SubDynamicDisplay = (props) => {
  const designContext = useContext(DesignContext)
  const { designerUi } = designContext
  let { schema, uischema } = props
  let elements = getDynamicElements(uischema)
  const renderers = useMemo(
    () => createDisplayRenderer(elements),
    [JSON.stringify(elements)]
  )

  let showUi = elements[0] || {}
  let showSchema = objectPath.get(
    schema,
    `properties.${uischema.name}.properties.${showUi.name}`
  )

  let editUi = elements[1] || {}
  let editSchema = objectPath.get(
    schema,
    `properties.${uischema.name}.properties.${editUi.name}`
  )

  let allPanelProps = {
    ...props,
    showSchema: showSchema,
    showUi: showUi,
    editSchema: editSchema,
    editUi: editUi,
    designerUi: designerUi,
  }

  return (
    <ComponentWrapper {...props}>
      <CustomHeader {...allPanelProps}>
        <PanelSample {...allPanelProps} renderers={renderers} />
      </CustomHeader>
    </ComponentWrapper>
  )
}

export const dynamicSystem = {
  tester: dynamicTester,
  renderer: withJsonFormsControlProps(DynamicSystem),
}

export const subDynamicSystem = {
  tester: subDynamicTester,
  renderer: withJsonFormsControlProps(SubDynamicSystem),
}

export const dynamicDisplay = {
  tester: dynamicTester,
  renderer: withJsonFormsControlProps(DynamicDisplay),
}

export const subDynamicDisplay = {
  tester: subDynamicTester,
  renderer: withJsonFormsControlProps(SubDynamicDisplay),
}
